import { SyntheticEvent, useCallback, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import { OrderStatusBadgeEnum } from "@/enums/orderStatusBadge";
import { Edit } from "@mui/icons-material";
import useAppUrl from "@/app/hooks/useAppUrl";
import CartToProductListSidebar from "@/app/pages/cart/cartToProductListSidebar";
import dataService from "@/app/services/dataService";
import ItemSourceEnum from "@/enums/itemSourceEnum";
import ProductListEnumType from "@/enums/productListEnumType";
import { useGlobalNotification } from "../header/GlobalNotificationContext";
import RequestModalConfig from "@/app/interfaces/RequestModalConfig";
import RequestModal from "../modal/RequestModal";
import Order from "@/app/interfaces/Order";
import ModalHelper from "@/app/helpers/ModalHelper";
import { ModalTypeEnum } from "@/enums/modalTypeEnum";
import StyleHelper from "@/app/helpers/StyleHelper";
import { NotificationTypeEnum } from "@/enums/notificationTypeEnum";
import RequestModalData from "@/app/interfaces/RequestModalData";
import { isEagleViewProduct } from "@/app/models/constants";
import { NoteLocationEnum } from "@/enums/noteLocationEnum";
import { LoadingStatusEnum } from "@/enums/loadingStatusEnum";
import { useMutation } from "@tanstack/react-query";
import { InforStatusLabelEnum } from "@/enums/InforStatusLabelEnum";
import DateHelper from "@/app/helpers/DateHelper";
import defaultImage from "@/img/default-image_210.png";
import { useNavigate } from "react-router-dom";
import { ERROR_MESSAGE } from "@/app/constants/constants";
import DowloadHelper from "@/app/helpers/DownloadHelper";
import PageLoadingOverlay from "../shared/pageLoadingOverlay";
import {
  List,
  ListItem,
  ListItemText,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CommonHelper from "@/app/helpers/CommonHelper";
import React from "react";
import ReturnModal from "../modal/ReturnModal";
import { isSAPSource } from "@/app/utils/erpHelper";

const SUCCESS_MESSAGE =
  "Thank you for reaching out. A team member will reach out and assist you with this change.";
const DOWNLOAD_ERROR = "Failed to download file.";
const LOADING_MESSAGE = "We are preparing your file. Please wait.";
const QUOTE_LABEL = "Quoted prices are valid for 30 days";

const OrderCard = ({
  order,
  defaultExpanded = false,
}: {
  order: Order;
  defaultExpanded?: boolean;
}) => {
  const { getAppUrl } = useAppUrl();
  const { setNotification } = useGlobalNotification();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isReturnModalOpen, setIsReturnModalOpen] = useState<boolean>(false);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState<boolean>(false);
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatusEnum>(
    LoadingStatusEnum.IDLE
  );
  const [requestModalConfig, setRequestModalConfig] =
    useState<RequestModalConfig | null>(null);
  const [isAccordionExpanded, setIsAccordionExpanded] =
    useState<boolean>(defaultExpanded);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const orderHasEagleView = order.items.some((item) => isEagleViewProduct(item.productSku));

  const handleReorder = () => {
    reorderMutate();
  };

  const onHandleAccordionChangeHandler = (
    event: SyntheticEvent,
    newExpanded: boolean
  ) => {
    if (isMobile) {
      return null;
    }

    setIsAccordionExpanded(newExpanded);
  };

  const { mutate: reorderMutate, isPending } = useMutation({
    mutationFn: () => dataService.reorder(order.id),
    onSuccess: (data) => {
      if (data.success) {
        setNotification(
          <>
            We have added the eligible items for Re-Order to your{" "}
            <a href="/cart" style={{ color: "#54afe2" }}>
              Shopping Cart
            </a>
          </>,
          NotificationTypeEnum.SUCCESS
        );
        window.dispatchEvent(new Event("refresh-cart-silent"));
      } else {
        setNotification(ERROR_MESSAGE, NotificationTypeEnum.ERROR);
      }
    },
    onError: () => {
      setNotification(ERROR_MESSAGE, NotificationTypeEnum.ERROR);
    },
  });

  const downloadClientPDF = useMutation({
    mutationFn: async () => {
      const result = await dataService.getExportOrderDataForPdf(order.id);

      if (result.success) {
        DowloadHelper.downloadOrdersPdf([result.data]);
      } else {
        setNotification(DOWNLOAD_ERROR, NotificationTypeEnum.ERROR);
      }
    },
    onError: () => {
      setNotification(DOWNLOAD_ERROR, NotificationTypeEnum.ERROR);
    },
  });

  const addOrderToTemplate = useMutation({
    mutationFn: async ({
      productListId,
      type,
    }: {
      productListId: string;
      type?: ProductListEnumType;
    }) => {
      if (type === ProductListEnumType.QuoteList) {
        return await dataService.AddProductToEstimate({
          estimateId: productListId,
          source: ItemSourceEnum.Order,
          orderId: order.id,
        });
      }

      return await dataService.AddProductToTemplates({
        templateId: productListId,
        source: ItemSourceEnum.Order,
        orderId: order.id,
      });
    },

    onSuccess: (response, { type }) => {
      const { success, data } = response || {};
      const {
        warnings = [],
        id,
        productListName: name,
        newProductListId,
        newProductListName,
        successMessage,
      } = data || {};

      if (success && warnings.length === 0) {
        const listTypeText =
          type === ProductListEnumType.QuoteList
            ? "Estimate List"
            : "Product Template";

        const url = `${process.env.REACT_APP_WEB_URL}/ProductListItem/List/${id}`;

        if (type === ProductListEnumType.BranchList) {
          const newUrl = `${process.env.REACT_APP_WEB_URL}/ProductListItem/List/${newProductListId}`;

          setNotification(
            <>
              Successfully added the product to the {listTypeText}{" "}
              <a href={newUrl}>{newProductListName}</a>.{" "}
              <a href={url}>{name}</a> has been archived
            </>,
            "success"
          );
          return;
        }

        setNotification(
          <>
            {successMessage.includes("{0}") ? (
              successMessage.split("{0}").map((part, index, arr) => (
                <React.Fragment key={index}>
                  {part}
                  {index < arr.length - 1 && <a href={url}>{name}</a>}
                </React.Fragment>
              ))
            ) : (
              <>
                {successMessage} <a href={url}>{name}</a>
              </>
            )}
          </>,
          "success"
        );
      } else {
        setNotification(warnings[0] ?? "Failed to add product.", "error");
      }
    },

    onError: () => {
      setNotification(
        "An unexpected error occurred while adding the product.",
        "error"
      );
    },

    onSettled: () => {
      setSidebarOpen(false);
    },
  });

  const isSAPOrder = isSAPSource(order.source ?? "");

  const handleRequestReturn = useCallback((e: SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isSAPOrder) {
      openRequestModal(
        ModalHelper.getRequestModalConfig(order, ModalTypeEnum.SapOrderReturn)
      );
    } else {
      setIsReturnModalOpen(true);
    }
  }, []);

  const addOrderToNewTemplate = useMutation({
    mutationFn: async ({
      name,
      description,
      type,
    }: {
      name?: string;
      description?: string;
      type?: ProductListEnumType;
    }) => {
      if (!type || !name) throw new Error("Missing name or type");

      return await dataService.createNewEstimateOrTemplate(
        type,
        name.trim(),
        (description ?? "").trim()
      );
    },

    onSuccess: async (response, { type, name }) => {
      if (
        response.success &&
        (!response.data.errorMessage ||
          response.data.errorMessage.trim() === "")
      ) {
        await addOrderToTemplate.mutateAsync({
          productListId: response.data.id?.toString() ?? "0",
          type,
        });
      } else if (
        response.data.errorMessage &&
        response.data.errorMessage.trim() !== ""
      ) {
        setNotification(response.data.errorMessage, "error");
      }
    },

    onError: () => {
      setNotification(
        "An unexpected error occurred while adding the product.",
        "error"
      );
    },

    onSettled: () => {
      setSidebarOpen(false);
    },
  });

  const openRequestModal = (config: RequestModalConfig) => {
    setRequestModalConfig(config);
    setIsRequestModalOpen(true);
  };

  const { mutateAsync: sendOrderChangeRequest } = useMutation({
    mutationFn: (data: RequestModalData) => {
      const payload = {
        ...data,
        noteLocation: NoteLocationEnum.ORDER_HISTORY,
      };

      return dataService.sendOrderChangeRequest(payload, order.id);
    },
    onMutate: () => {
      setLoadingStatus(LoadingStatusEnum.LOADING);
    },
    onSettled: () => {
      setIsRequestModalOpen(false);
      setRequestModalConfig(null);
      setLoadingStatus(LoadingStatusEnum.IDLE);
    },
    onSuccess: () => {
      setNotification(SUCCESS_MESSAGE, NotificationTypeEnum.SUCCESS);
    },
    onError: () => {
      setNotification(ERROR_MESSAGE, NotificationTypeEnum.ERROR);
    },
  });

  const handleSendRequest = async (data: RequestModalData) => {
    await sendOrderChangeRequest(data);
  };

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        backgroundColor: "#fff",
        boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <Accordion
        expanded={isAccordionExpanded}
        onChange={onHandleAccordionChangeHandler}
        elevation={0}
      >
        <AccordionSummary
          sx={{
            cursor: "default !important",
            "& *": { userSelect: "text" },
            "& .MuiAccordionSummary-content": {
              userSelect: "text",
            },
            "& .MuiTypography-root": {},
            "& .MuiSvgIcon-root": {
              userSelect: "none",
            },
          }}
          aria-controls={`panel-${order.id}-content`}
          id={`panel-${order.id}-header`}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "center", md: "baseline" },
              mr: 2,
              mt: { xs: 0.5, md: 0 },
            }}
          >
            {isAccordionExpanded ? (
              <ExpandLessIcon
                sx={{ color: "#aeaeae", display: { xs: "none", sm: "block" } }}
              />
            ) : (
              <ExpandMoreIcon
                sx={{ color: "#aeaeae", display: { xs: "none", sm: "block" } }}
              />
            )}
          </Box>
          <Grid container alignItems="center" sx={{ width: "100%" }}>
            <Grid
              container
              spacing={2}
              sx={{ flexDirection: { xs: "column", sm: "row" }, mb: 2 }}
            >
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                sx={{ display: "flex", justifyContent: "flex-start" }}
              >
                <Box>
                  <Typography
                    fontSize={"20px"}
                    sx={{
                      fontWeight: "normal",
                      color: "#333",
                      mb: { xs: 1, sm: 0 },
                    }}
                  >
                    Job Name:{" "}
                    <Typography
                      sx={{
                        display: "inline-block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        verticalAlign: "bottom",
                        fontSize: "20px",
                        fontWeight: "bold",
                      }}
                    >
                      {order.jobName || "N/A"}
                    </Typography>
                  </Typography>
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={3}
                sx={{ display: "flex", justifyContent: "flex-start" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "flex-start", sm: "center" },
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: StyleHelper.getOrderStatusBadgeColors(
                        order.orderStatusBadge
                      ).background,
                      color: StyleHelper.getOrderStatusBadgeColors(
                        order.orderStatusBadge
                      ).color,
                      border: `2px solid ${
                        StyleHelper.getOrderStatusBadgeColors(
                          order.orderStatusBadge
                        ).color
                      }`,
                      padding: "5px 20px",
                      fontWeight: "600",
                      fontFamily: '"Montserrat",sans-serif',
                      fontSize: "13px",
                      borderRadius: "4px",
                    }}
                  >
                    {order.orderStatusBadge
                      ? order.orderStatusBadge.toUpperCase()
                      : ""}
                  </Box>
                  {order?.orderSubStatus !==
                    InforStatusLabelEnum.RETURN_IN_PROGRESS &&
                    StyleHelper.getInforStatusLabel(order?.statusDisplayName)
                      .label && (
                      <Box display="flex" alignItems="center" ml={1}>
                        {
                          StyleHelper.getInforStatusLabel(
                            order?.statusDisplayName
                          ).icon
                        }
                        <Typography
                          variant="body2"
                          sx={{
                            color: StyleHelper.getInforStatusLabel(
                              order?.statusDisplayName
                            ).color,
                            ml: 0.5,
                          }}
                        >
                          {
                            StyleHelper.getInforStatusLabel(
                              order?.statusDisplayName
                            ).label
                          }
                        </Typography>
                      </Box>
                    )}
                  {order?.orderSubStatus !== order?.statusDisplayName &&
                    StyleHelper.getInforStatusLabel(order?.orderSubStatus)
                      .label && (
                      <Box display="flex" alignItems="center" ml={1}>
                        {
                          StyleHelper.getInforStatusLabel(order?.orderSubStatus)
                            .icon
                        }
                        <Typography
                          variant="body2"
                          sx={{
                            color: StyleHelper.getInforStatusLabel(
                              order?.orderSubStatus
                            ).color,
                            ml: 0.5,
                          }}
                        >
                          {
                            StyleHelper.getInforStatusLabel(
                              order?.orderSubStatus
                            ).label
                          }
                        </Typography>
                      </Box>
                    )}
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={3}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "flex-start", sm: "flex-end" },
                  }}
                >
                  <Typography fontSize={"20px"} sx={{ color: "#333" }}>
                    Order Total:{" "}
                    <Typography
                      fontSize={"20px"}
                      component="span"
                      fontWeight="bold"
                    >
                      {CommonHelper.addDolarSign(order.orderTotal)}
                    </Typography>
                  </Typography>
                </Box>
              </Grid>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {order.orderSubStatus === "Quoted" &&
                  order.orderType === "Quote" && (
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontFamily: '"Montserrat", Verdana, sans-serif',
                        fontSize: "12px",
                      }}
                    >
                      {QUOTE_LABEL}
                    </Typography>
                  )}
              </Box>
            </Grid>

            <Grid
              container
              item
              xs={12}
              spacing={1}
              fontSize={"14px"}
              sx={{
                color: "#555",
                pt: { xs: 2, sm: 0 },
              }}
            >
              <Grid item xs={12} md={3}>
                <Box>
                  <Typography fontSize="14px" sx={{ width: "100%" }}>
                    Order #:{" "}
                    <Typography
                      fontSize="14px"
                      component="span"
                      fontWeight="bold"
                    >
                      {order.orderNumber}
                    </Typography>
                  </Typography>
                </Box>

                <Box>
                  <Typography fontSize="14px" sx={{ width: "100%" }}>
                    Order Date:{" "}
                    <Typography
                      fontSize="14px"
                      component="span"
                      fontWeight="bold"
                    >
                      {DateHelper.toDisplayDate(order.createdOn)}
                    </Typography>
                  </Typography>
                </Box>

                <Box>
                  <Typography fontSize="14px" sx={{ width: "100%" }}>
                    Order Type:{" "}
                    <Typography
                      fontSize="14px"
                      component="span"
                      fontWeight="bold"
                      overflow="wrap"
                    >
                      {order.orderType}
                    </Typography>
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={3}>
                <Box>
                  <Typography fontSize="14px" sx={{ width: "max-content" }}>
                    Order Status:{" "}
                    <Typography
                      fontSize="14px"
                      component="span"
                      fontWeight="bold"
                    >
                      {order.statusDisplayName}
                    </Typography>
                  </Typography>
                </Box>

                <Box>
                  <Typography fontSize="14px" sx={{ width: "100%" }}>
                    Order Ready Date:{" "}
                    <Typography
                      fontSize="14px"
                      component="span"
                      fontWeight="bold"
                    >
                      {DateHelper.toDisplayDate(order.dateFilled)}
                    </Typography>
                  </Typography>
                </Box>

                <Box>
                  <Typography fontSize="14px" sx={{ width: "max-content" }}>
                    Invoice Date:{" "}
                    <Typography
                      fontSize="14px"
                      component="span"
                      fontWeight="bold"
                    >
                      {DateHelper.toDisplayDate(order.invoiceDate)}
                    </Typography>
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={3}>
                <Box>
                  <Typography fontSize="14px" sx={{ width: "max-content" }}>
                    Order Location:{" "}
                    <Typography
                      fontSize="14px"
                      component="span"
                      fontWeight="bold"
                    >
                      {order.orderLocation}
                    </Typography>
                  </Typography>
                </Box>

                <Box>
                  <Typography fontSize="14px" sx={{ width: "100%" }}>
                    Ship To:{" "}
                    <Typography
                      fontSize="14px"
                      component="span"
                      fontWeight="bold"
                    >
                      {order.shipToName}
                    </Typography>
                  </Typography>
                </Box>

                <Box>
                  <Typography fontSize="14px" sx={{ width: "max-content" }}>
                    Pickup/Delivery:{" "}
                    <Typography
                      fontSize="14px"
                      component="span"
                      fontWeight="bold"
                    >
                      {order.pickupInStore ? "Pickup" : "Delivery"}
                    </Typography>
                  </Typography>
                </Box>

                {order?.selectedDeliveryDate && (
                  <Box>
                    <Typography fontSize="14px" sx={{ width: "max-content" }}>
                      Delivery Date:{" "}
                      <Typography
                        fontSize="14px"
                        component="span"
                        fontWeight="bold"
                      >
                        {DateHelper.toDisplayDate(order.selectedDeliveryDate)}
                      </Typography>
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} md={3}>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  alignItems={{ xs: "flex-start", md: "flex-end" }}
                  height="100%"
                  top={{ xs: 0, md: -4 }}
                  position="relative"
                >
                  {OrderStatusBadgeEnum.IN_PROGRESS ===
                    order.orderStatusBadge && (
                    <Link
                      href="#"
                      className="request-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        openRequestModal(
                          ModalHelper.getRequestModalConfig(
                            order,
                            ModalTypeEnum.OrderChange
                          )
                        );
                      }}
                      fontSize={"14px"}
                      sx={{
                        textDecoration: "none",
                        cursor: "pointer",
                        color: "#007bff",
                        mb: 0.5,
                        fontFamily: '"Montserrat", Verdana, sans-serif',
                        display: { xs: "none", sm: "flex" },
                      }}
                    >
                      <Edit
                        sx={{
                          fontSize: "14px",
                          verticalAlign: "middle",
                          mr: 0.5,
                        }}
                      />
                      Request Order Change
                    </Link>
                  )}
                  {OrderStatusBadgeEnum.IN_PROGRESS ===
                    order.orderStatusBadge && (
                    <Link
                      href="#"
                      className="request-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        openRequestModal(
                          ModalHelper.getRequestModalConfig(
                            order,
                            ModalTypeEnum.StatusUpdate
                          )
                        );
                      }}
                      fontSize={"14px"}
                      sx={{
                        textDecoration: "none",
                        color: "#007bff",
                        fontFamily: '"Montserrat", Verdana, sans-serif',
                        display: { xs: "none", sm: "flex" },
                      }}
                    >
                      <Edit
                        sx={{
                          fontSize: "14px",
                          verticalAlign: "middle",
                          mr: 0.5,
                        }}
                      />
                      Request Status Update
                    </Link>
                  )}
                  {OrderStatusBadgeEnum.COMPLETE === order.orderStatusBadge && (
                    <Link
                      href="#"
                      className="request-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        openRequestModal(
                          ModalHelper.getRequestModalConfig(
                            order,
                            ModalTypeEnum.InvoiceChange
                          )
                        );
                      }}
                      fontSize={"14px"}
                      sx={{
                        textDecoration: "none",
                        color: "#007bff",
                        fontFamily: '"Montserrat", Verdana, sans-serif',
                        display: { xs: "none", sm: "flex" },
                      }}
                    >
                      <Edit
                        sx={{
                          fontSize: "14px",
                          verticalAlign: "middle",
                          mr: 0.5,
                        }}
                      />
                      Request Invoice Change
                    </Link>
                  )}
                  {OrderStatusBadgeEnum.QUOTED === order.orderStatusBadge && (
                    <Link
                      className="request-link"
                      href="#"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        openRequestModal(
                          ModalHelper.getRequestModalConfig(
                            order,
                            ModalTypeEnum.QuoteChange
                          )
                        );
                      }}
                      fontSize={"14px"}
                      sx={{ textDecoration: "none", color: "#007bff", mb: 0.5 }}
                    >
                      <Edit
                        sx={{
                          fontSize: "14px",
                          verticalAlign: "middle",
                          mr: 0.5,
                        }}
                      />
                      Request Quote Change
                    </Link>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails sx={{ borderTop: "1px solid #eee", pt: 2, pb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={9}>
              <Box
                sx={{
                  display: { xs: "none", sm: "flex" },
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {order.items.length > 0 ? (
                  <>
                    {order.items.slice(0, 2).map((product, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "flex-start", sm: "center" },
                          justifyContent: {
                            xs: "flex-start",
                            sm: "space-between",
                          },
                          gap: "16px",
                          fontSize: "14px",
                          color: "#444",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: "16px",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              width: "100px",
                              height: "100px",
                              borderRadius: "4px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              flexShrink: 0,
                              mb: { xs: 1, sm: 0 },
                              overflow: "hidden",
                            }}
                          >
                            <img
                              alt={product.productName ?? "No image"}
                              src={product.pictureUrl ?? defaultImage}
                              loading="lazy"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                backgroundColor:
                                  product.colorHex || "transparent",
                              }}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: "4px",
                                fontFamily: '"Montserrat", Verdana, sans-serif',
                              }}
                            >
                              <Typography fontSize={"14px"}>
                                Product:{" "}
                                {product.isAvailable ? (
                                  <Link
                                    fontSize={"14px"}
                                    className="request-link"
                                    sx={{
                                      fontWeight: "bold",
                                      textDecoration: "none",
                                    }}
                                    href={
                                      product.parentGroupedProductId
                                        ? `/lbpcustomer/product?id=${product.parentGroupedProductId}&associatedproduct=${product.productId}&uom=${product.productValueAttributeId}`
                                        : `/lbpcustomer/product?id=${product.productId}&uom=${product.productValueAttributeId}`
                                    }
                                    target="_blank"
                                  >
                                    {product.productName}
                                  </Link>
                                ) : (
                                  <span style={{ fontWeight: "bold" }}>
                                    {product.productName}
                                  </span>
                                )}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                fontFamily: '"Montserrat", Verdana, sans-serif',
                              }}
                            >
                              <Typography fontSize={"14px"}>SKU:</Typography>
                              <Typography fontSize={"14px"} fontWeight="bold">
                                {isSAPOrder
                                  ? product.sapProduct
                                  : product.productSku}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                fontFamily: '"Montserrat", Verdana, sans-serif',
                              }}
                            >
                              <Typography fontSize={"14px"}>UOM:</Typography>
                              <Typography fontSize={"14px"} fontWeight="bold">
                                {product.uom}
                              </Typography>
                            </Box>
                            {product.color && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <Typography fontSize={"14px"}>
                                  Color:
                                </Typography>
                                <Typography fontSize={"14px"} fontWeight="bold">
                                  {product.color}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            alignItems: "flex-start",
                            gap: "4px",
                            minWidth: { sm: "200px" },
                          }}
                        >
                          {product.orderItemSpecificationModelItems &&
                            product.orderItemSpecificationModelItems.map(
                              (item, index) => (
                                <Stack
                                  key={index}
                                  direction="row"
                                  spacing={0.5}
                                  sx={{
                                    mt: 0.25,
                                    ml: 0.5,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ display: "flex", gap: 1 }}
                                  >
                                    <span style={{ fontWeight: 600 }}>Qty</span>
                                    {` ${item.qty ?? 0} `}
                                    <span style={{ fontWeight: 600 }}>
                                      * Length
                                    </span>
                                    {` ${item.lengthFt ?? 0} `}
                                    <span style={{ fontWeight: 600 }}>Ft</span>
                                    {` ${item.lengthIn ?? 0} `}
                                    <span style={{ fontWeight: 600 }}>In</span>
                                  </Typography>
                                </Stack>
                              )
                            )}
                        </Box>
                      </Box>
                    ))}

                    {order.itemsCount > 2 && (
                      <Button
                        size="small"
                        variant="text"
                        className="request-link"
                        sx={{
                          alignSelf: "flex-start",
                          mt: 1,
                          fontSize: "14px",
                        }}
                        onClick={() => navigate(`/order/details/${order.id}`)}
                      >
                        + {order?.itemsCount - 2} MORE ITEMS
                      </Button>
                    )}
                  </>
                ) : (
                  <Typography fontSize={"14px"} sx={{ color: "#777" }}>
                    No products listed for this order.
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                justifyContent: "flex-start",
                alignItems: { xs: "stretch", md: "flex-end" },
                mt: { xs: 2, md: 0 },
              }}
            >
              <Box
                display={"flex"}
                flexDirection={"column"}
                gap={1}
                sx={{ xs: { width: "100%" }, md: { width: "300px" } }}
              >
                <Button
                  fullWidth
                  className="blueButtonSolid"
                  onClick={() => navigate(`/order/details/${order.id}`)}
                >
                  {order.orderStatusBadge === OrderStatusBadgeEnum.QUOTED
                    ? "VIEW QUOTE DETAILS"
                    : "VIEW ORDER DETAILS"}
                </Button>
                {order.orderSubStatus ===
                  InforStatusLabelEnum.NEEDS_DELIVERY_SCHEDULED && (
                  <Button
                    sx={{
                      display: {
                        xs: "none !important",
                        sm: "flex !important",
                      },
                    }}
                    fullWidth
                    className="successButtonSolid"
                    onClick={() => navigate(`/order/details/${order.id}`)}
                  >
                    SCHEDULE DELIVERY
                  </Button>
                )}
                {order.orderStatusBadge === OrderStatusBadgeEnum.QUOTED ? (
                  <>
                    <Button
                      fullWidth
                      className="blueButtonOutline"
                      href={getAppUrl(`/onepagecheckout?quoteId=${order.id}`)}
                      sx={{
                        textAlign: "center",
                        justifyContent: "center",
                        display: {
                          xs: "none !important",
                          sm: "flex !important",
                        },
                      }}
                    >
                      ORDER NOW
                    </Button>
                    <Button
                      fullWidth
                      className="blueButtonOutline"
                      href={getAppUrl(`/Order/EditQuote?quoteId=${order.id}`)}
                      sx={{ textAlign: "center", justifyContent: "center" }}
                    >
                      EDIT QUOTE
                    </Button>
                  </>
                ) : (
                  <>
                    {!orderHasEagleView && order.showAddToTemplateButton && (
                      <Button
                        fullWidth
                        className="blueButtonOutline"
                        sx={{
                          display: {
                            xs: "none !important",
                            sm: "flex !important",
                          },
                        }}
                        onClick={() => {
                          if (!order.reorderEnabled) {
                            setNotification(
                              "All products on the order are not available for reorder, and cannot be added to Templates or Estimates.",
                              NotificationTypeEnum.ERROR
                            );
                          } else setSidebarOpen(true);
                        }}
                      >
                        ADD TO TEMPLATE OR ESTIMATE
                      </Button>
                    )}
                    {!orderHasEagleView &&
                      !order.isReturnMerchandise &&
                      order.reorderEnabled &&
                      order.orderStatusBadge !==
                        OrderStatusBadgeEnum.QUOTED && (
                        <Button
                          sx={{
                            display: {
                              xs: "none !important",
                              sm: "flex !important",
                            },
                          }}
                          fullWidth
                          className="blueButtonOutline"
                          onClick={handleReorder}
                          disabled={isPending}
                        >
                          {isPending ? "RE-ORDERING" : "RE-ORDER"}
                        </Button>
                      )}
                  </>
                )}
                <Button
                  fullWidth
                  className="blueButtonOutline"
                  onClick={() => downloadClientPDF.mutate()}
                >
                  DOWNLOAD PDF
                </Button>
                {(order.canBeReturned || order.isOutsideReturnedTimeframe) &&
                  order.isReturnsTesting && (
                    <Button
                      fullWidth
                      className="request-link"
                      onClick={handleRequestReturn}
                      sx={{
                        display: {
                          xs: "none !important",
                          sm: "flex !important",
                        },
                        color: "#007bff",
                        justifyContent: "center",
                        textDecoration: "underline",
                        p: 0,
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      REQUEST RETURN
                    </Button>
                  )}
              </Box>
            </Grid>
            {order.relatedQuotes?.length > 0 && (
              <Grid
                item
                xs={12}
                md={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <div>
                  <Typography
                    fontWeight="bold"
                    sx={{ borderBottom: "1px solid black", mb: 1 }}
                  >
                    Related Quotes:
                  </Typography>

                  <List dense sx={{ p: 0, width: 380 }}>
                    {order.relatedQuotes.map((quote) => (
                      <ListItem
                        key={quote.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          px: 0,
                          width: "100%",
                        }}
                      >
                        <ListItemText
                          primary={quote.name}
                          primaryTypographyProps={{
                            sx: {
                              textTransform: "uppercase",
                              fontFamily: '"Montserrat",Verdana,sans-serif',
                              fontSize: "14px",
                            },
                          }}
                        />
                        <Typography
                          sx={{
                            color: "#54afe2",
                            cursor: "pointer",
                            fontFamily: '"Montserrat",Verdana,sans-serif',
                            fontSize: "14px",
                          }}
                          onClick={() => navigate(`/order/details/${quote.id}`)}
                        >
                          View detail
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </div>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
      <CartToProductListSidebar
        open={sidebarOpen}
        onCartToNewTemplate={(
          name?: string,
          description?: string,
          type?: ProductListEnumType
        ) => {
          if (name && type) {
            addOrderToNewTemplate.mutate({ name, description, type });
          }
        }}
        onCartToTemplate={(id?: string, type?: ProductListEnumType) => {
          if (id && type) {
            addOrderToTemplate.mutate({ productListId: id, type });
          }
        }}
        onClose={() => {
          setSidebarOpen(false);
        }}
      />

      {isRequestModalOpen && requestModalConfig && (
        <RequestModal
          open={isRequestModalOpen}
          loadingStatus={loadingStatus}
          onClose={() => {
            setIsRequestModalOpen(false);
            setRequestModalConfig(null);
          }}
          onSend={handleSendRequest}
          {...requestModalConfig}
        />
      )}
      <ReturnModal
        open={isReturnModalOpen}
        orderId={order.id}
        onClose={() => setIsReturnModalOpen(false)}
      />
      {downloadClientPDF.isPending && (
        <PageLoadingOverlay text={LOADING_MESSAGE} />
      )}

      {(addOrderToTemplate.isPending || addOrderToNewTemplate.isPending) && (
        <PageLoadingOverlay />
      )}
    </Box>
  );
};

export default OrderCard;
